import { memo, useState, useCallback } from 'react';
import { Drawer, Tabs, Button, Text, Badge } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconUsers, IconMail, IconUserPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { WorkspaceMembersList } from '../WorkspaceMembersList';
import { WorkspaceInvitationsList } from '../WorkspaceInvitationsList';
import { BatchInvitationModal } from '../BatchInvitationModal';
import { useWorkspaceMembers, useWorkspaceInvitations } from '../../../hooks';
import { useGeoMarkupStore } from '../../../store';
import { canManageMembers } from '../../../types/workspace';
import {
  FONT_WEIGHT_SEMIBOLD,
  ICON_SIZE_SM,
  ICON_SIZE_TAB,
  TEAM_PANEL_DRAWER_WIDTH,
} from '../../../constants/ui';
import type {
  WorkspaceListItem,
  WorkspaceRole,
} from '../../../types/workspace';
import styles from './WorkspaceManagementPanel.module.css';

type ManagementTab = 'members' | 'invitations';

interface WorkspaceManagementPanelProps {
  opened: boolean;
  workspace: WorkspaceListItem | null;
  currentUserRole: WorkspaceRole;
  currentUserId: string;
  activeTab?: ManagementTab;
  onClose: () => void;
  onTabChange?: (tab: ManagementTab) => void;
}

export const WorkspaceManagementPanel = memo(function WorkspaceManagementPanel({
  opened,
  workspace,
  currentUserRole,
  currentUserId,
  activeTab = 'members',
  onClose,
  onTabChange,
}: WorkspaceManagementPanelProps) {
  const { t } = useTranslation();
  const isPhone = useMediaQuery('(max-width: 48em)');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const canManage = workspace?.isOwner || canManageMembers(currentUserRole);

  const members = useWorkspaceMembers({
    workspaceId: workspace?.workspaceId || null,
    autoFetch: opened,
  });

  const invitations = useWorkspaceInvitations({
    workspaceId: workspace?.workspaceId || null,
    autoFetch: opened && canManage,
  });

  const userStatuses = useGeoMarkupStore(
    (state) => state.presence.userStatuses
  );

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (value) {
        onTabChange?.(value as ManagementTab);
      }
    },
    [onTabChange]
  );

  const openInviteModal = useCallback(() => setShowInviteModal(true), []);

  const closeInviteModal = useCallback(() => setShowInviteModal(false), []);

  if (!workspace) {
    return null;
  }

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size={isPhone ? '100%' : TEAM_PANEL_DRAWER_WIDTH}
        title={
          <Text fw={FONT_WEIGHT_SEMIBOLD}>
            {canManage
              ? t('workspace.teamManagement')
              : t('workspace.teamMembers')}
          </Text>
        }
        padding="md"
        withCloseButton
        classNames={{ body: styles.drawerBody }}
      >
        {canManage ? (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={styles.tabs}
          >
            <Tabs.List grow>
              <Tabs.Tab value="members">
                <IconUsers size={ICON_SIZE_TAB} />
                {t('workspace.members.active')}
                <Badge
                  size="sm"
                  variant="light"
                  circle
                  className={styles.tabBadge}
                >
                  {workspace.memberCount}
                </Badge>
              </Tabs.Tab>
              <Tabs.Tab value="invitations">
                <IconMail size={ICON_SIZE_TAB} />
                {t('workspace.members.pending')}
                <Badge
                  size="sm"
                  variant="light"
                  circle
                  className={styles.tabBadge}
                >
                  {invitations.totalCount}
                </Badge>
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="members" className={styles.tabPanel}>
              <WorkspaceMembersList
                members={members.members}
                hasNextPage={members.hasNextPage}
                isLoading={members.isLoading}
                hasLoaded={members.hasLoaded}
                search={members.search}
                roleFilter={members.roleFilter}
                currentUserRole={currentUserRole}
                currentUserId={currentUserId}
                onSearchChange={members.setSearch}
                onRoleFilterChange={members.setRoleFilter}
                onLoadMore={members.loadMore}
                onRoleChange={members.updateMemberRole}
                onRemove={members.removeMember}
                userStatuses={userStatuses}
                error={members.error}
              />
            </Tabs.Panel>

            <Tabs.Panel value="invitations" className={styles.tabPanel}>
              <WorkspaceInvitationsList
                invitations={invitations.invitations}
                hasNextPage={invitations.hasNextPage}
                isLoading={invitations.isLoading}
                hasLoaded={invitations.hasLoaded}
                search={invitations.search}
                statusFilter={invitations.statusFilter}
                roleFilter={invitations.roleFilter}
                currentUserRole={currentUserRole}
                onSearchChange={invitations.setSearch}
                onStatusFilterChange={invitations.setStatusFilter}
                onRoleFilterChange={invitations.setRoleFilter}
                onLoadMore={invitations.loadMore}
                onResend={invitations.resendInvitation}
                onCancel={invitations.cancelInvitation}
                error={invitations.error}
              />
            </Tabs.Panel>
          </Tabs>
        ) : (
          <WorkspaceMembersList
            members={members.members}
            hasNextPage={members.hasNextPage}
            isLoading={members.isLoading}
            hasLoaded={members.hasLoaded}
            search={members.search}
            roleFilter={members.roleFilter}
            currentUserRole={currentUserRole}
            currentUserId={currentUserId}
            onSearchChange={members.setSearch}
            onRoleFilterChange={members.setRoleFilter}
            onLoadMore={members.loadMore}
            onRoleChange={members.updateMemberRole}
            onRemove={members.removeMember}
            userStatuses={userStatuses}
            error={members.error}
          />
        )}

        {canManage && (
          <Button
            fullWidth
            leftSection={<IconUserPlus size={ICON_SIZE_SM} />}
            onClick={openInviteModal}
            className={styles.inviteButton}
          >
            {t('workspace.invitations.inviteNewPerson')}
          </Button>
        )}
      </Drawer>

      <BatchInvitationModal
        opened={showInviteModal}
        onClose={closeInviteModal}
        onSend={invitations.sendBatchInvitations}
        isSending={invitations.isSending}
        workspaceName={workspace.name}
      />
    </>
  );
});
